import { z } from "zod";

import { ArchivedView, ListOwnership } from "@/enum/list.enum";

import { ALL_SOURCES } from "../../List/components/SourceTypePalette";
import { CampaignChannelFilter, CampaignStatusFilter } from "./campaignFilters";

/**
 * Zod schema for the Campaigns overview filters a "pinned" tab restores (PREC-3880).
 *
 * The Campaigns sibling of the Lead List overview's `PinnedTabFiltersSchema`. Both are stored in
 * the same per-user table, told apart by the row's `scope` — which is why the shapes may diverge
 * freely: the Campaigns overview filters by campaign *status* and *channel* where the List
 * overview filters by content type.
 */
export const CampaignPinnedTabFiltersSchema = z.object({
	/** Scope the tab restores (My / Team / All campaigns). */
	ownership: z.nativeEnum(ListOwnership),
	/** Campaign status filter. */
	statusFilter: z.nativeEnum(CampaignStatusFilter),
	/** Channel filter. */
	channelFilter: z.nativeEnum(CampaignChannelFilter),
	/** Archived visibility. */
	archivedView: z.nativeEnum(ArchivedView),
	/** Normalised source type — `undefined` means "any source". */
	sourceType: z.string().optional(),
	/** Free-text search term — `undefined`/empty means "no search". */
	search: z.string().optional(),
});

export type CampaignPinnedTabFilters = z.infer<typeof CampaignPinnedTabFiltersSchema>;

/** A user-named pinned Campaigns filter tab, persisted per-user on the backend. */
export interface CampaignPinnedFilterTab {
	id: number;
	name: string;
	filters: CampaignPinnedTabFilters;
}

/**
 * Normalise the source-type control's value for storage.
 *
 * The Campaigns filter bar models "any source" as the {@link ALL_SOURCES} sentinel string, but a
 * snapshot stores it as `undefined` (matching the List overview payload). Without this the
 * sentinel would be persisted verbatim and equality checks against a restored tab would never
 * match, so the pinned tab could never light up as active.
 */
export const normalizeCampaignSourceType = (sourceType: string | undefined): string | undefined =>
	sourceType && sourceType !== ALL_SOURCES ? sourceType : undefined;

/**
 * Whether the given (already-normalised) filters are worth pinning. A tab is only worth saving
 * when at least one *secondary* filter is non-default — a free-text search term, or a non-default
 * status / channel / archived / source filter. A bare ownership change is excluded: the built-in
 * My / Team / All tabs already reach those scopes, so pinning one would duplicate a tab the user
 * already has.
 */
export const isCampaignFilterPinnable = (filters: CampaignPinnedTabFilters): boolean =>
	!!filters.search?.trim() ||
	filters.statusFilter !== CampaignStatusFilter.ALL ||
	filters.channelFilter !== CampaignChannelFilter.ALL ||
	filters.archivedView !== ArchivedView.HIDE ||
	filters.sourceType !== undefined;

/** Structural equality of two Campaigns filter snapshots. */
export const areCampaignPinnedFiltersEqual = (
	a: CampaignPinnedTabFilters,
	b: CampaignPinnedTabFilters
): boolean =>
	a.ownership === b.ownership &&
	a.statusFilter === b.statusFilter &&
	a.channelFilter === b.channelFilter &&
	a.archivedView === b.archivedView &&
	(a.sourceType ?? undefined) === (b.sourceType ?? undefined) &&
	(a.search?.trim() || undefined) === (b.search?.trim() || undefined);

/**
 * The id of the pinned tab whose snapshot exactly matches the current filters, or `undefined` when
 * none match. Used both to light the active pinned tab as the user adjusts filters and to stop a
 * second pin of an identical view from creating a duplicate.
 */
export const findMatchingCampaignPinnedTabId = (
	tabs: CampaignPinnedFilterTab[],
	filters: CampaignPinnedTabFilters
): number | undefined =>
	tabs.find((tab) => areCampaignPinnedFiltersEqual(tab.filters, filters))?.id;
